/**
 * Каркас кабинета партнёра: шапка, разделы и меню пользователя на каждом экране.
 *
 * Своя обёртка, а не портальный `Page`: у партнёра нет ни ленты, ни оргструктуры, ни процессов,
 * зато нужны вещи, которых у портала нет — разделы со счётчиками и выход к списку из карточки.
 * До этого из карточки поездки можно было уйти только кнопкой браузера.
 *
 * Счётчики берутся из тех же запросов, что и сами списки (`staleTime` 30 с общий), поэтому шапка
 * не добавляет сетевой работы: на экране списка запрос уже в кэше.
 */
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { ScrollView, Stack, Text, XStack, YStack } from 'tamagui'

import { api } from '../../api/client'
import { useAuth } from '../../auth/useAuth'
import { useApp } from '../../i18n'
import { useResponsive } from '../../lib/responsive'
import { tone } from '../../theme/tones'
import { Avatar, Backdrop, Icon, Link } from '../../ui'
import { pressable } from '../../ui/primitives'

export type CabinetSection = 'trips' | 'contracts'

/**
 * Выход к списку. Назад по истории, а при прямом заходе (ссылка из письма) — в раздел: из
 * карточки поездки раньше можно было уйти только кнопкой браузера.
 */
export function CabinetBack({ to, label }: { to: CabinetSection; label: string }) {
  const router = useRouter()
  return (
    <Link
      fontSize={14}
      alignSelf="flex-start"
      onPress={() => (router.canGoBack() ? router.back() : router.replace(`/cabinet/${to}`))}
    >
      {`← ${label}`}
    </Link>
  )
}

const MAX_WIDTH = 1120

/** Знак портала: тот же квадрат с монограммой, что в шапке корпоративного контура. */
function Brand() {
  const { l } = useApp()
  return (
    <XStack alignItems="center" gap={10} flexShrink={0}>
      <Stack
        width={32}
        height={32}
        borderRadius={8}
        backgroundColor="$primary"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize={13} fontWeight="800" color="$onPrimary">
          AS
        </Text>
      </Stack>
      <YStack>
        <Text fontSize={14} fontWeight="700" color="$color">
          AS Life
        </Text>
        <Text fontSize={12} color="$muted">
          {l('cab.shell.title')}
        </Text>
      </YStack>
    </XStack>
  )
}

/** Раздел в шапке: подпись, счётчик и подчёркивание активного. */
function SectionTab({
  label,
  count,
  active,
  onPress,
}: {
  label: string
  count: number
  active: boolean
  onPress: () => void
}) {
  return (
    <XStack
      {...pressable}
      role="tab"
      aria-selected={active}
      aria-current={active ? 'page' : undefined}
      onPress={onPress}
      alignItems="center"
      gap={8}
      height={48}
      minHeight={44}
      paddingHorizontal={12}
      borderBottomWidth={3}
      borderColor={active ? '$primary' : 'transparent'}
    >
      <Text fontSize={15} fontWeight={active ? '700' : '600'} color={active ? '$primary' : '$muted'}>
        {label}
      </Text>
      <Stack
        minWidth={22}
        height={22}
        paddingHorizontal={7}
        borderRadius={999}
        alignItems="center"
        justifyContent="center"
        backgroundColor={active ? '$primary' : '$borderSecondary'}
      >
        <Text fontSize={12} fontWeight="700" color={active ? '$onPrimary' : '$muted'}>
          {count}
        </Text>
      </Stack>
    </XStack>
  )
}

export function CabinetShell({
  section,
  children,
}: {
  /** Активный раздел; не задан — страница вне разделов (приглашение коллеги). */
  section?: CabinetSection
  children: ReactNode
}) {
  const { l } = useApp()
  const router = useRouter()
  const { logout } = useAuth()
  const { isPhone } = useResponsive()
  const [menu, setMenu] = useState(false)

  const profile = useQuery({ queryKey: ['cabinet', 'me'], queryFn: api.cabinetMe })
  const trips = useQuery({ queryKey: ['cabinet', 'trips'], queryFn: api.cabinetTrips })
  const contracts = useQuery({ queryKey: ['cabinet', 'contracts'], queryFn: api.cabinetContracts })

  const me = profile.data
  const go = (s: CabinetSection) => {
    setMenu(false)
    router.replace(`/cabinet/${s}`)
  }
  const tabs = (
    <>
      <SectionTab
        label={l('cab.nav.trips')}
        count={trips.data?.length ?? 0}
        active={section === 'trips'}
        onPress={() => go('trips')}
      />
      <SectionTab
        label={l('cab.nav.contracts')}
        count={contracts.data?.length ?? 0}
        active={section === 'contracts'}
        onPress={() => go('contracts')}
      />
    </>
  )

  /** Пункты меню одни и те же на вебе и на телефоне — меняется только его положение. */
  const menuItems = (
    <YStack role="menu" gap={2}>
      <Text fontSize={12} color="$muted" paddingHorizontal={12} paddingVertical={10}>
        {me ? `ВÖİN ${me.organization.voen} · ${l(`cab.role.${me.role}`)}` : ''}
      </Text>
      {/* Приглашение — право ответственного; остальным кнопку не рисуем, но решает бэк. */}
      {me?.role === 'RESPONSIBLE' && (
        <XStack
          {...pressable}
          role="menuitem"
          onPress={() => {
            setMenu(false)
            router.push('/cabinet/invite')
          }}
          alignItems="center"
          height={44}
          paddingHorizontal={12}
          borderRadius={6}
        >
          <Text fontSize={14} color="$color">
            {l('cab.invite.title')}
          </Text>
        </XStack>
      )}
      <XStack
        {...pressable}
        role="menuitem"
        onPress={() => {
          setMenu(false)
          void logout()
        }}
        alignItems="center"
        height={44}
        paddingHorizontal={12}
        borderRadius={6}
      >
        <Text fontSize={14} color={tone('red').text}>
          {l('cab.logout')}
        </Text>
      </XStack>
    </YStack>
  )

  return (
    <Stack flex={1} backgroundColor="$bgLayout">
      <YStack backgroundColor="$background" borderBottomWidth={1} borderColor="$borderColor">
        <XStack
          width="100%"
          maxWidth={MAX_WIDTH}
          alignSelf="center"
          paddingHorizontal={16}
          minHeight={64}
          alignItems="center"
          gap={12}
        >
          <Brand />
          {!isPhone && (
            <XStack role="tablist" aria-label={l('cab.nav.label')} flex={1} gap={4}>
              {tabs}
            </XStack>
          )}
          <XStack flex={isPhone ? 1 : 0} justifyContent="flex-end">
            <XStack
              {...pressable}
              role="button"
              aria-haspopup="menu"
              aria-expanded={menu}
              aria-label={l('cab.shell.menu')}
              onPress={() => setMenu((v) => !v)}
              alignItems="center"
              gap={8}
              height={44}
              paddingHorizontal={6}
              borderRadius={10}
            >
              <Avatar name={me?.fullName ?? '—'} size="sm" />
              {!isPhone && (
                <YStack maxWidth={200}>
                  <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>
                    {me?.fullName ?? ''}
                  </Text>
                  <Text fontSize={12} color="$muted" numberOfLines={1}>
                    {me?.organization.name ?? ''}
                  </Text>
                </YStack>
              )}
              <Icon name="chevron-down" size={16} color="$muted" />
            </XStack>
          </XStack>
        </XStack>
        {/* На телефоне разделы уходят под шапку: в одну строку с брендом и меню они не влезают. */}
        {isPhone && (
          <XStack
            role="tablist"
            aria-label={l('cab.nav.label')}
            paddingHorizontal={8}
            gap={4}
            borderTopWidth={1}
            borderColor="$borderSecondary"
          >
            {tabs}
          </XStack>
        )}
      </YStack>

      <ScrollView flex={1} contentContainerStyle={{ alignItems: 'center' }}>
        <YStack width="100%" maxWidth={MAX_WIDTH} padding={16} gap={16}>
          {children}
        </YStack>
      </ScrollView>

      {menu && (
        <>
          <Backdrop onPress={() => setMenu(false)} />
          {/* Телефон — нижний лист, веб — панель под аватаром: на узком экране выпадашка
              у правого края обрезается, а лист всегда достаётся большим пальцем. */}
          <YStack
            position="absolute"
            zIndex={50}
            {...(isPhone
              ? { left: 0, right: 0, bottom: 0, borderTopLeftRadius: 14, borderTopRightRadius: 14 }
              : { right: 16, top: 60, width: 280, borderRadius: 10 })}
            backgroundColor="$background"
            borderWidth={1}
            borderColor="$borderColor"
            padding={6}
          >
            {menuItems}
          </YStack>
        </>
      )}
    </Stack>
  )
}
